import { DateTime } from 'luxon';
import {
    BranchChangeNotification,
    LeaveRequestApproveNotification,
    LeaveRequestDeleteNotification,
    LeaveRequestRejectNotification,
    LeaveRequestUpdateOnGoingNotification,
    PolicyDeletion,
    PolicyMembersChanged,
    PolicyChangeNotification,
    RoleChangeNotification,
    UploadDocumentNotification,
    LeaveRequestCreationNotification,
    LoyaltyProgramBonusNotification,
    PolicyNameUpdatedNotification,
    MedicalLeaveCreationNotification,
    MedicalLeaveRequestApproveUpdate,
    MedicalLeaveRequestRejecteUpdate,
    MedicalLeaveRequestEnded,
    MedicalLeaveUpdatedNotification,
} from '../../commonTypes/NotificationTypes/userService.types';
import { TranslationFormatter } from './TranslationFormatter';
import {
    ApproveRejectUpdateNotification,
    EvaluationUpdateRequestedNotification,
    PerformedEvaluationNotification,
    RequestedEvaluationNotification,
} from '../../commonTypes/NotificationTypes/evaluationService.types';
import { EvaluationStatus } from '../../commonTypes/evaluation/calculate.types';

export class NotificationUserService {
    private formatter: TranslationFormatter;

    constructor(formatter: TranslationFormatter) {
        this.formatter = formatter;
    }

    public generateLeaveRequestApprovementMessage(notification: LeaveRequestApproveNotification, userSub: string): string {
        const { policyType, fromDate, toDate, numOfDays, remainingDays } = notification.message;

        if (userSub === notification.initiatorUserSub) {
            return this.formatter.getTranslation('notificationMessages.leaveRequestApprove.initiator', {
                leaveRequestType: policyType,
                fromDate: DateTime.fromISO(fromDate).toFormat('dd.MM.yyyy.'),
                toDate: DateTime.fromISO(toDate).toFormat('dd.MM.yyyy.'),
                numOfDays: numOfDays,
                recipientFullName: this.formatter.getFullName(notification.recipients[0].recipient),
                initiatorFullName: this.formatter.getFullName(notification.initiators),
            });
        }

        return this.formatter.getTranslation('notificationMessages.leaveRequestApprove.recipient', {
            leaveRequestType: policyType,
            fromDate: DateTime.fromISO(fromDate).toFormat('dd.MM.yyyy.'),
            toDate: DateTime.fromISO(toDate).toFormat('dd.MM.yyyy.'),
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            numOfDays: numOfDays,
            remainingDays: remainingDays,
        });
    }

    public generateLeaveRequestRejectionMessage(notification: LeaveRequestRejectNotification, userSub: string): string {
        const { policyType, fromDate, toDate, numOfDays, remainingDays } = notification.message;

        if (userSub === notification.initiatorUserSub) {
            return this.formatter.getTranslation('notificationMessages.leaveRequestReject.initiator', {
                leaveRequestType: policyType,
                fromDate: DateTime.fromISO(fromDate).toFormat('dd.MM.yyyy.'),
                toDate: DateTime.fromISO(toDate).toFormat('dd.MM.yyyy.'),
                numOfDays: numOfDays,
                initiatorFullName: this.formatter.getFullName(notification.initiators),
            });
        }

        return this.formatter.getTranslation('notificationMessages.leaveRequestReject.recipient', {
            leaveRequestType: policyType,
            fromDate: DateTime.fromISO(fromDate).toFormat('dd.MM.yyyy.'),
            toDate: DateTime.fromISO(toDate).toFormat('dd.MM.yyyy.'),
            numOfDays: numOfDays,
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            remainingDays: remainingDays,
        });
    }

    public generateLeaveRequestDeletionMessage(notification: LeaveRequestDeleteNotification): string {
        const { numOfDays, fromDate, toDate } = notification.message;
        return this.formatter.getTranslation('notificationMessages.leaveRequestDeleted', {
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            numOfDays: numOfDays,
            fromDate: DateTime.fromISO(fromDate).toFormat('dd.MM.yyyy.'),
            toDate: DateTime.fromISO(toDate).toFormat('dd.MM.yyyy.'),
        });
    }

    public generateLeaveRequestUpdateOnGoingMessage(notification: LeaveRequestUpdateOnGoingNotification): string {
        const { policyType, fromDate, newDateTo, numOfDays } = notification.message;

        return this.formatter.getTranslation('notificationMessages.leaveRequestUpdateOnGoing', {
            leaveRequestType: policyType,
            fromDate: DateTime.fromISO(fromDate).toFormat('dd.MM.yyyy.'),
            newDateTo: DateTime.fromISO(newDateTo).toFormat('dd.MM.yyyy.'),
            numOfDays: numOfDays,
            initiatorFullName: this.formatter.getFullName(notification.initiators),
        });
    }

    public generatePolicyDeletionMessage(notification: PolicyDeletion): string {
        const { policyName } = notification.message;
        return this.formatter.getTranslation('notificationMessages.deletePolicy', { policyName });
    }

    public generatePolicyMembersChangeMessage(notificaiton: PolicyMembersChanged): string {
        const { policyName } = notificaiton.message;

        return this.formatter.getTranslation('notificationMessages.policyMembersChange', { policyName });
    }

    public generatePolicyChangeMessage(notification: PolicyChangeNotification): string {
        const { policyName, oldAmount, newAmount } = notification.message;
        return this.formatter.getTranslation('notificationMessages.policyChange', {
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            policyName,
            oldAmount,
            newAmount,
        });
    }

    public generateBranchChangeMessage(notification: BranchChangeNotification): string {
        const { oldBranchName, newBranchName } = notification.message;
        return this.formatter.getTranslation('notificationMessages.branchChange', {
            oldBranchName,
            newBranchName,
        });
    }

    public generateRoleChangeMessage(notification: RoleChangeNotification): string {
        const { oldRole, newRole } = notification.message;
        return this.formatter.getTranslation('notificationMessages.roleChange', {
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            oldRole,
            newRole,
        });
    }

    public generateDocumentUploadMessage(notification: UploadDocumentNotification): string {
        const { name } = notification.message;
        return this.formatter.getTranslation('notificationMessages.documentUpload', {
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            name,
        });
    }

    public generateLeaveRequestCreationMessage(notification: LeaveRequestCreationNotification): string {
        const { numOfDays, fromDate, toDate, policyName } = notification.message;
        return this.formatter.getTranslation('notificationMessages.leaveRequestCreation', {
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            numOfDays: numOfDays,
            policyName: policyName,
            fromDate: DateTime.fromISO(fromDate).toFormat('dd.MM.yyyy.'),
            toDate: DateTime.fromISO(toDate).toFormat('dd.MM.yyyy.'),
        });
    }
    public generateLoyaltyProgramBonusMessage(notification: LoyaltyProgramBonusNotification, userSub: string): string {
        if (userSub === notification.initiatorUserSub) {
            return this.formatter.getTranslation('notificationMessages.loyaltyProgramBonus.initiator', {
                bonusDaysAwarded: notification.message.bonusDaysAwarded,
                bonusEligibilityYears: notification.message.bonusEligibilityYears,
                policyName: notification.message.policyName,
                plural: notification.message.bonusDaysAwarded > 1 ? 's' : '',
                pluralSerbian1: notification.message.bonusDaysAwarded > 1 ? 'a' : '',
                pluralSerbian2: notification.message.bonusDaysAwarded > 1 ? 'a' : 'е',
            });
        }

        return this.formatter.getTranslation('notificationMessages.loyaltyProgramBonus.recipient', {
            bonusDaysAwarded: notification.message.bonusDaysAwarded,
            bonusEligibilityYears: notification.message.bonusEligibilityYears,
            policyName: notification.message.policyName,
            initiatorFullName: this.formatter.getFullName(notification.initiators),
            plural: notification.message.bonusDaysAwarded > 1 ? 's' : '',
            pluralSerbian1: notification.message.bonusDaysAwarded > 1 ? 'a' : '',
            pluralSerbian2: notification.message.bonusDaysAwarded > 1 ? 'a' : 'е',
        });
    }

    public generatePolicyNameUpdatedMessage(notification: PolicyNameUpdatedNotification): string {
        const { oldName, newName } = notification.message;
        return this.formatter.getTranslation('notificationMessages.POLICY_NAME_UPDATED', { oldName, newName });
    }

    public generateMedicalLeaveRequestCreationMessage(notification: MedicalLeaveCreationNotification): string {
        const { familyName, givenName, startDate } = notification.message;
        return this.formatter.getTranslation('notificationMessages.medicalLeaveRequestCreation', {
            givenName: givenName,
            familyName: familyName,
            startDate: DateTime.fromISO(startDate).toFormat('dd.MM.yyyy.'),
        });
    }

    public generateMedicalLeaveRequestApprovementMessage(notification: MedicalLeaveRequestApproveUpdate, userSub: string): string {
        const { startDate } = notification.message;

        if (userSub === notification.initiatorUserSub) {
            return this.formatter.getTranslation('notificationMessages.medicalLeaveRequestApprove.initiator', {
                startDate: DateTime.fromISO(startDate).toFormat('dd.MM.yyyy.'),
                recipientFullName: this.formatter.getFullName(notification.recipients[0].recipient),
                initiatorFullName: this.formatter.getFullName(notification.initiators),
            });
        }

        return this.formatter.getTranslation('notificationMessages.medicalLeaveRequestApprove.recipient', {
            startDate: DateTime.fromISO(startDate).toFormat('dd.MM.yyyy.'),
            initiatorFullName: this.formatter.getFullName(notification.initiators),
        });
    }

    public generateMedicalLeaveRequestRejectionMessage(notification: MedicalLeaveRequestRejecteUpdate, userSub: string): string {
        const { startDate } = notification.message;

        if (userSub === notification.initiatorUserSub) {
            return this.formatter.getTranslation('notificationMessages.medicalLeaveRequestReject.initiator', {
                startDate: DateTime.fromISO(startDate).toFormat('dd.MM.yyyy.'),
                initiatorFullName: this.formatter.getFullName(notification.initiators),
            });
        }

        return this.formatter.getTranslation('notificationMessages.medicalLeaveRequestReject.recipient', {
            startDate: DateTime.fromISO(startDate).toFormat('dd.MM.yyyy.'),
            initiatorFullName: this.formatter.getFullName(notification.initiators),
        });
    }

    public generateMedicalLeaveRequestEndedMessage(notification: MedicalLeaveRequestEnded): string {
        const { endDate } = notification.message;

        return this.formatter.getTranslation('notificationMessages.medicalLeaveRequestEnded.recipient', {
            endDate: DateTime.fromISO(endDate).toFormat('dd.MM.yyyy.'),
            initiatorFullName: this.formatter.getFullName(notification.initiators),
        });
    }

    generateMedicalLeaveRequestUpdatedMessage(notification: MedicalLeaveUpdatedNotification): string {
        const { description, startDate, endDate } = notification.message;

        const formattedStartDate = DateTime.fromISO(startDate).toFormat('dd.MM.yyyy.');
        const formattedEndDate = endDate ? DateTime.fromISO(endDate).toFormat('dd.MM.yyyy.') : null;
        const dateRange = formattedEndDate ? `${formattedStartDate} - ${formattedEndDate}` : formattedStartDate;

        return this.formatter.getTranslation('notificationMessages.medicalLeaveRequestUpdated', {
            description,
            dateRange,
            initiatorFullName: this.formatter.getFullName(notification.initiators),
        });
    }

    public generateEvaluationUpdateRequestedMessage(notification: EvaluationUpdateRequestedNotification): string {
        return this.formatter.getTranslation('notificationMessages.evaluationUpdateRequested.recipient', {
            evaluateeFullName: this.formatter.getFullName(notification.initiators),
        });
    }
    public generateEvaluationRequestedEvaluationMessage(notification: RequestedEvaluationNotification, userSub: string): string {
        if (notification.message.evaluatorUserSub === notification.message.evaluateeUserSub) {
            return this.formatter.getTranslation('notificationMessages.evaluationRequested.selfEvaluation');
        }
        if (userSub === notification.message.evaluatorUserSub) {
            if (notification.message.isAnonymous) {
                return this.formatter.getTranslation('notificationMessages.evaluationRequested.evaluatorAnonymous', {
                    evaluateeFullName: notification.message.evaluateeFullName,
                });
            } else {
                return this.formatter.getTranslation('notificationMessages.evaluationRequested.evaluator', {
                    evaluateeFullName: notification.message.evaluateeFullName,
                });
            }
        }

        if (notification.message.isAnonymous) {
            return this.formatter.getTranslation('notificationMessages.evaluationRequested.evaluateeAnonymous');
        } else {
            return this.formatter.getTranslation('notificationMessages.evaluationRequested.evaluatee', {
                evaluatorFullName: notification.message.evaluatorFullName,
            });
        }
    }
    public generateEvaluationPerformedEvaluationMessage(notification: PerformedEvaluationNotification): string {
        if (notification.recipients[0].recipient.userSub === notification.message.evaluatorUserSub) {
            if (notification.message.previousStatus === EvaluationStatus.UPDATE_APPROVED) {
                return this.formatter.getTranslation('notificationMessages.evaluationPerformed.selfEvaluationUpdateApproved');
            } else {
                return this.formatter.getTranslation('notificationMessages.evaluationPerformed.selfEvaluation');
            }
        }

        if (notification.message.isAnonymous) {
            if (notification.message.previousStatus === EvaluationStatus.UPDATE_APPROVED) {
                return this.formatter.getTranslation('notificationMessages.evaluationPerformed.evaluationPerformedAnonymousUpdateApproved');
            } else {
                return this.formatter.getTranslation('notificationMessages.evaluationPerformed.evaluationPerformedAnonymous');
            }
        } else {
            if (notification.message.previousStatus === EvaluationStatus.UPDATE_APPROVED) {
                return this.formatter.getTranslation('notificationMessages.evaluationPerformed.evaluationPerformedUpdateApproved', {
                    evaluatorFullName: notification.message.evaluatorFullName,
                });
            } else {
                return this.formatter.getTranslation('notificationMessages.evaluationPerformed.evaluationPerformed', {
                    evaluatorFullName: notification.message.evaluatorFullName,
                });
            }
        }
    }

    public generateApproveRejectUpdateMessage(notification: ApproveRejectUpdateNotification, userSub: string): string {
        const { status } = notification.message;
        const isApproved = status === 'UPDATE_APPROVED';
        const recipient = notification.recipients[0]?.recipient;

        if (userSub === notification.initiatorUserSub && userSub === recipient.userSub) {
            if (!recipient) {
                return this.formatter.getTranslation('notificationMessages.unknownNotification');
            }
            if (isApproved) {
                return this.formatter.getTranslation('notificationMessages.approveRejectUpdate.initiatorApproved', {
                    evaluatorFullName: this.formatter.getFullName(recipient),
                });
            } else {
                return this.formatter.getTranslation('notificationMessages.approveRejectUpdate.initiatorRejected', {
                    evaluatorFullName: this.formatter.getFullName(recipient),
                });
            }
        }

        if (isApproved) {
            return this.formatter.getTranslation('notificationMessages.approveRejectUpdate.recipientApproved', {
                approverFullName: this.formatter.getFullName(notification.initiators),
            });
        } else {
            return this.formatter.getTranslation('notificationMessages.approveRejectUpdate.recipientRejected', {
                rejectorFullName: this.formatter.getFullName(notification.initiators),
            });
        }
    }
}
